import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useResourceById } from "@/hooks/resource-by-id.hook";
import { useResourceLibrary } from "@/hooks/resource-library.hook";
import { dateFromServer } from "@/lib/utils";
import { format } from "date-fns";
import { ExportSquare } from "iconsax-react";
import { Download } from "lucide-react";
import { FC } from "react";
import { Skeleton } from "../ui/skeleton";

type PreviewResourceDrawerProps = {
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  resourceId: string;
};

const PreviewResourceDrawer: FC<PreviewResourceDrawerProps> = ({
  resourceId,
  ...props
}) => {
  const handleOpenChange = (isOpen: boolean) => {
    props.onOpenChange?.(isOpen);
  };

  const { data: resourceLibrary } = useResourceLibrary();

  const { data: resource, isLoading: isResourceLoading } = useResourceById({
    companyId: resourceLibrary?.owner.id || "",
    resourceLibraryId: resourceLibrary?.id || "",
    resourceId: resourceId || "",
  });

  const SHORT_DATE_FORMAT = import.meta.env.VITE_SHORT_DATE_FORMAT;

  return (
    <Sheet open={props.isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="flex flex-col">
        <SheetHeader>
          <SheetTitle>
            {resource
              ? resource.type === "LINK"
                ? "Detalle del enlace"
                : "Detalle del documento"
              : "Detalle del recurso"}
          </SheetTitle>
          <SheetDescription className="hidden">
            Publica un link en la biblioteca de recursos
          </SheetDescription>
        </SheetHeader>

        <SheetBody>
          {/* <h3 className="mb-4 text-lg font-bold text-[#615CB7]">
            Información general
          </h3> */}

          {isResourceLoading ? (
            <div className="flex flex-col gap-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton
                  key={index}
                  className="w-full"
                  style={{ height: "40px" }}
                />
              ))}
            </div>
          ) : (
            <>
              {resource && (
                <>
                  <div className="flex flex-col gap-4">
                    <div>
                      <Label>Nombre del recurso</Label>
                      <p className="text-sm text-[#4F5D6D]">{resource.name}</p>
                    </div>

                    {resource.type === "LINK" && (
                      <div>
                        <Label>URL</Label>
                        <p className="text-sm text-[#4F5D6D]">
                          <a
                            className="break-all underline hover:text-black"
                            href={resource.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExportSquare
                              className="mr-1 inline-block"
                              size={14}
                            />

                            {resource.url}
                          </a>
                        </p>
                      </div>
                    )}

                    {resource.type === "FILE" && (
                      <div>
                        <Label>Archivo</Label>
                        <p className="text-sm text-[#4F5D6D]">
                          <a
                            className="break-all underline hover:text-black"
                            href={resource.signedUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Download className="mr-1 inline-block" size={14} />
                            {resource.fileName}
                          </a>
                        </p>
                      </div>
                    )}

                    <div>
                      <Label>
                        {resource.type === "LINK"
                          ? "Fecha de creación"
                          : "Fecha de subida"}
                      </Label>
                      <p className="text-sm text-[#4F5D6D]">
                        {format(
                          dateFromServer(resource.uploadDate),
                          SHORT_DATE_FORMAT,
                        )}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};

export default PreviewResourceDrawer;
